"use client";

import Link from "next/link";
import { StarRating } from "@/components/shared/star-rating";
import { cn } from "@/lib/utils/cn";
import { formatAUM, stripMarkdown } from "@/lib/utils/format";

type FundCardProps = {
  slug: string;
  schemeName: string;
  amc: string;
  category: string;
  wtmScore: number;
  expenseRatio: number;
  returns1Y: number | null;
  returns3Y: number | null;
  aum: number | null;
  currency?: string;
  summaryText?: string;
};

export function FundCard({
  slug,
  schemeName,
  amc,
  category,
  wtmScore,
  expenseRatio,
  returns1Y,
  returns3Y,
  aum,
  currency = "INR",
  summaryText,
}: FundCardProps) {
  return (
    <Link
      href={`/funds/${slug}`}
      className="bg-surface border border-surface-light/30 rounded-xl p-4 hover:border-blue/40 transition no-underline group block"
    >
      {/* Top row — name + score */}
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="min-w-0 flex-1">
          <span className="text-foreground font-serif text-sm group-hover:text-blue transition block truncate">
            {schemeName}
          </span>
          <span className="text-dim text-[11px]">{amc}</span>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <span className="text-foreground font-bold text-sm font-tabular">
            {wtmScore.toFixed(1)}
          </span>
          <StarRating score={wtmScore} size="text-xs" />
        </div>
      </div>

      {/* Category badge */}
      <div className="mb-2">
        <span className="bg-blue/20 text-blue text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
          {category}
        </span>
      </div>

      {/* Metrics row */}
      <div className="flex items-center gap-3 text-dim text-[11px] mb-2">
        {returns1Y !== null && (
          <span>
            1Y:{" "}
            <span
              className={cn(
                "font-semibold font-tabular",
                returns1Y >= 0 ? "text-cgreen" : "text-cred"
              )}
            >
              {returns1Y > 0 ? "+" : ""}
              {returns1Y.toFixed(1)}%
            </span>
          </span>
        )}
        {returns3Y !== null && (
          <>
            <span>&bull;</span>
            <span>
              3Y:{" "}
              <span
                className={cn(
                  "font-semibold font-tabular",
                  returns3Y >= 0 ? "text-cgreen" : "text-cred"
                )}
              >
                {returns3Y > 0 ? "+" : ""}
                {returns3Y.toFixed(1)}%
              </span>
            </span>
          </>
        )}
        <span>&bull;</span>
        <span>ER: {expenseRatio.toFixed(2)}%</span>
        <span>&bull;</span>
        <span>AUM: {formatAUM(aum, currency)}</span>
      </div>

      {/* Summary excerpt — strip markdown delimiters so raw **bold** and
          headings don't leak into the browse grid. */}
      {summaryText && (
        <p className="text-muted-foreground text-[12px] leading-relaxed line-clamp-2">
          {stripMarkdown(summaryText)}
        </p>
      )}
    </Link>
  );
}
